/**
 * VIN Decoder Pro — admin dashboard for the Phase 2 reseller service.
 *
 * Permission tiers (matching the backend):
 *   - super_admin (TelcaVoIP / Fabrizio): sees + edits EVERYTHING
 *       including provider cost, markup price, packages catalogue,
 *       master enable/disable, and full margin breakdown.
 *   - admin (LoJack ops): sees the consumption dashboard, can buy
 *       packages, but NEVER sees provider cost or margin.
 *   - it (LoJack IT): read-only consumption view + low-balance alerts
 *       via email.
 *
 * The page falls back gracefully when the feature is OFF: it shows
 * the master switch with a clear explanation and the available
 * packages catalogue so the admin can preview what's coming.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, AlertCircle, Award, CalendarClock, CheckCircle2, ChevronLeft, ChevronRight,
  Clock, CreditCard, Loader2, Lock, MapPin, Power, RefreshCw, Send,
  Settings as SettingsIcon, ShieldCheck, Sparkles, Store, TrendingUp, Trophy, Wallet,
} from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const formatEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const VinDecoderProPage = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState(null); // super_admin only
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSwitch, setSavingSwitch] = useState(false);
  const [topupBusy, setTopupBusy] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [hallOfFame, setHallOfFame] = useState(null);
  const [hofMonth, setHofMonth] = useState(null);
  const [hofLoading, setHofLoading] = useState(false);
  const [dealersVinPro, setDealersVinPro] = useState(null);
  const [packagesPublic, setPackagesPublic] = useState([]); // public catalogue (admin-safe, no cost/margin leak)
  const [stripeStatus, setStripeStatus] = useState(null); // {phase: 'polling'|'paid'|'failed'|'expired', message, package}
  const pollRef = useRef(null);

  const role = user?.role || null;
  const isSuperAdmin = role === "super_admin";
  // Defense-in-depth: the TelcaVoIP-margin card (Configurazione pricing)
  // exposes the commercial markup over Vincario — strictly confidential
  // to the platform owner (TelcaVoIP). Even if a future role change
  // accidentally grants ``super_admin`` to a LoJack address, the card
  // stays hidden unless the email domain ALSO matches.
  const isTelcavoip = (user?.email || "").toLowerCase().endsWith("@telcavoip.eu");
  const canSeeMargin = isSuperAdmin && isTelcavoip;
  const canBuyPackages = ["super_admin", "admin"].includes(role);

  // Note: role is read directly from useAuth on each render — no memo needed.

  const refreshAll = async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API}/admin/vin-decoder/usage`),
        axios.get(`${API}/admin/vin-decoder/burn-forecast?days=7`),
        axios.get(`${API}/admin/vin-decoder/hall-of-fame?limit=5`),
        axios.get(`${API}/admin/vin-decoder/dealers-vin-pro`),
        axios.get(`${API}/vin-decoder/packages`),
      ];
      if (canSeeMargin) calls.push(axios.get(`${API}/admin/vin-decoder/settings`));
      if (canBuyPackages) calls.push(axios.get(`${API}/admin/vin-decoder/transactions?limit=20`));
      const results = await Promise.allSettled(calls);
      if (results[0].status === "fulfilled") setUsage(results[0].value.data);
      if (results[1].status === "fulfilled") setForecast(results[1].value.data);
      if (results[2].status === "fulfilled") {
        setHallOfFame(results[2].value.data);
        setHofMonth(results[2].value.data.month);
      }
      if (results[3].status === "fulfilled") setDealersVinPro(results[3].value.data);
      if (results[4].status === "fulfilled") setPackagesPublic(results[4].value.data.packages || []);
      if (canSeeMargin && results[5]?.status === "fulfilled") setSettings(results[5].value.data);
      const txIdx = canSeeMargin ? 6 : 5;
      if (canBuyPackages && results[txIdx]?.status === "fulfilled") {
        setTransactions(results[txIdx].value.data.transactions || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDealerVinPro = async (dealerId, body) => {
    try {
      await axios.patch(`${API}/admin/vin-decoder/dealers-vin-pro/${dealerId}`, body);
      const r = await axios.get(`${API}/admin/vin-decoder/dealers-vin-pro`);
      setDealersVinPro(r.data);
      toast.success(body.purchase_enabled
        ? "Dealer abilitato all'acquisto autonomo"
        : "Acquisto autonomo dealer disabilitato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento dealer");
    }
  };

  const loadHallOfFame = async (targetMonth) => {
    setHofLoading(true);
    try {
      const params = targetMonth ? `?month=${targetMonth}&limit=5` : "?limit=5";
      const r = await axios.get(`${API}/admin/vin-decoder/hall-of-fame${params}`);
      setHallOfFame(r.data);
      setHofMonth(r.data.month);
    } catch (e) {
      toast.error("Impossibile caricare la Hall of Fame");
    } finally {
      setHofLoading(false);
    }
  };

  useEffect(() => { refreshAll(); /* eslint-disable-next-line */ }, []);

  const toggleEnabled = async () => {
    if (!settings) return;
    setSavingSwitch(true);
    try {
      const next = !settings.enabled;
      const ok = window.confirm(
        next
          ? "Attivare il VIN Decoder Pro?\n\nDal momento dell'attivazione le richieste dei dealer al provider verranno fatturate a TelcaVoIP secondo il prezzo per query configurato.\n\nProcedere?"
          : "Disattivare il VIN Decoder Pro?\n\nI dealer perderanno l'accesso al bottone di decodifica completa. Resterà attivo il WMI offline (gratis).\n\nProcedere?"
      );
      if (!ok) { setSavingSwitch(false); return; }
      const r = await axios.put(`${API}/admin/vin-decoder/settings`, { enabled: next });
      setSettings(r.data);
      toast.success(next ? "Servizio attivato" : "Servizio disattivato");
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally {
      setSavingSwitch(false);
    }
  };

  const buyPackage = async (pkg) => {
    const ok = window.confirm(
      `Acquistare il pacchetto "${pkg.name}"?\n\n` +
      `• ${pkg.queries} query / mese\n` +
      `• Prezzo: ${formatEur(pkg.price_eur)}\n\n` +
      `Verrai reindirizzato a Stripe per il pagamento sicuro. ` +
      `I crediti saranno accreditati automaticamente al ritorno.\n\nProcedere?`
    );
    if (!ok) return;
    setTopupBusy(pkg.id);
    try {
      const r = await axios.post(`${API}/vin-decoder/checkout-session`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        // Redirect to Stripe-hosted Checkout
        window.location.href = r.data.url;
      } else {
        toast.error("Risposta Stripe non valida");
        setTopupBusy(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore avvio checkout");
      setTopupBusy(null);
    }
  };

  // ── Stripe return-from-checkout polling ──────────────────────────
  // When the user comes back from Stripe Checkout with ?stripe_session_id=...
  // we poll the status endpoint until "paid" (max 5 attempts × 2s).
  // The backend webhook is the authoritative crediting path; this poll
  // is the secondary path that catches the user before the webhook lands.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancelled");

    if (cancelled === "1") {
      setStripeStatus({ phase: "cancelled", message: "Pagamento annullato. Nessun addebito effettuato." });
      // Clean URL so a refresh doesn't re-trigger the toast.
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;

    setStripeStatus({ phase: "polling", message: "Verifica pagamento in corso…" });
    let attempts = 0;
    const maxAttempts = 5;
    const interval = 2000;

    const tick = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/webhook/stripe/checkout-status/${sessionId}`);
        const ps = r.data.payment_status;
        if (ps === "paid") {
          setStripeStatus({
            phase: "paid",
            message: `Pagamento confermato — ${r.data.queries} crediti aggiunti.`,
            package: r.data.package_name,
          });
          toast.success("Pagamento completato!");
          window.history.replaceState({}, "", window.location.pathname);
          await refreshAll();
          return;
        }
        if (r.data.status === "expired") {
          setStripeStatus({ phase: "expired", message: "La sessione di pagamento è scaduta." });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (attempts >= maxAttempts) {
          setStripeStatus({
            phase: "pending",
            message: "Pagamento in elaborazione. Riceverai un'email a conferma; i crediti verranno aggiunti automaticamente.",
          });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        pollRef.current = setTimeout(tick, interval);
      } catch (err) {
        if (attempts >= maxAttempts) {
          setStripeStatus({
            phase: "failed",
            message: "Verifica stato pagamento non riuscita. Controlla la sezione Transazioni.",
          });
          return;
        }
        pollRef.current = setTimeout(tick, interval);
      }
    };
    tick();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  if (loading && !usage) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const balance = usage?.balance ?? 0;
  const threshold = usage?.low_balance_threshold ?? 100;
  const enabled = usage?.enabled;
  const last30 = usage?.last_30_days || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="vin-decoder-pro-page">
      <div className="border-b border-gray-800 bg-[#111]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/15 text-lojack-blue-400 flex items-center justify-center">
            <Activity size={20} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">VIN Decoder Pro</h1>
            <p className="text-gray-400 text-sm">
              Decodifica avanzata VIN tramite provider commerciale {canSeeMargin && settings?.provider ? `(${settings.provider})` : ""}.
              {!canSeeMargin && " Consumo prepagato: ogni decodifica decrementa il saldo."}
            </p>
          </div>
          <button onClick={refreshAll} className="px-3 py-2 bg-[#0a0a0a] border border-gray-700 hover:border-lojack-blue-500 rounded-xl text-sm flex items-center gap-2" data-testid="refresh-btn">
            <RefreshCw size={14} /> Aggiorna
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stripe return-from-checkout banner */}
        {stripeStatus && (
          <div
            className={`border rounded-2xl p-4 flex items-start gap-3 ${
              stripeStatus.phase === "paid"
                ? "bg-emerald-500/5 border-emerald-500/30"
                : stripeStatus.phase === "polling"
                ? "bg-lojack-blue-500/5 border-lojack-blue-500/30"
                : stripeStatus.phase === "cancelled"
                ? "bg-gray-500/5 border-gray-500/30"
                : "bg-lojack-clause-500/5 border-lojack-clause-500/30"
            }`}
            data-testid="stripe-status-banner"
          >
            {stripeStatus.phase === "paid" ? (
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
            ) : stripeStatus.phase === "polling" ? (
              <Loader2 size={18} className="text-lojack-blue-400 shrink-0 mt-0.5 animate-spin" />
            ) : (
              <AlertCircle size={18} className="text-lojack-clause-400 shrink-0 mt-0.5" />
            )}
            <div className="text-sm flex-1">
              <strong
                className={
                  stripeStatus.phase === "paid"
                    ? "text-emerald-300"
                    : stripeStatus.phase === "polling"
                    ? "text-lojack-blue-300"
                    : "text-lojack-clause-300"
                }
              >
                {stripeStatus.phase === "paid"
                  ? "Pagamento confermato"
                  : stripeStatus.phase === "polling"
                  ? "Verifica pagamento"
                  : stripeStatus.phase === "cancelled"
                  ? "Checkout annullato"
                  : stripeStatus.phase === "expired"
                  ? "Sessione scaduta"
                  : stripeStatus.phase === "pending"
                  ? "Pagamento in elaborazione"
                  : "Errore"}
              </strong>
              <p className="text-gray-300 mt-1">{stripeStatus.message}</p>
            </div>
            <button
              onClick={() => setStripeStatus(null)}
              className="text-gray-500 hover:text-white text-xs"
              data-testid="stripe-status-dismiss"
            >
              Chiudi
            </button>
          </div>
        )}

        {/* Master switch (TelcaVoIP-only — costs are charged to TelcaVoIP) */}
        {canSeeMargin && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5 flex items-center gap-4" data-testid="master-switch-card">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${enabled ? "bg-lojack-blue-500/15 text-lojack-blue-400" : "bg-gray-800/50 text-gray-500"}`}>
              <Power size={22} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Master Switch — {enabled ? "ATTIVO" : "DISATTIVO"}</h3>
              <p className="text-gray-500 text-xs mt-0.5">
                {enabled
                  ? "Le richieste vengono inoltrate al provider. I costi vengono addebitati a TelcaVoIP."
                  : "I dealer non vedono il bottone 'Decodifica completa'. Resta operativo il WMI offline (gratis)."}
              </p>
            </div>
            <button
              onClick={toggleEnabled}
              disabled={savingSwitch}
              className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition ${enabled ? "bg-red-600 hover:bg-red-700" : "bg-lojack-blue-600 hover:bg-lojack-blue-700"} disabled:opacity-50`}
              data-testid="master-switch-toggle"
            >
              {savingSwitch ? "…" : (enabled ? "Disattiva" : "Attiva")}
            </button>
          </div>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2">
              <Wallet size={14} /> Saldo crediti
            </div>
            <div className={`text-3xl font-bold ${balance < threshold ? "text-lojack-clause-400" : "text-lojack-blue-400"}`} data-testid="balance-value">
              {balance.toLocaleString("it-IT")}
            </div>
            {balance < threshold && balance > 0 && (
              <p className="text-lojack-clause-400/80 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> Sotto soglia ({threshold})
              </p>
            )}
            {balance === 0 && (
              <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> Saldo esaurito — feature pausata
              </p>
            )}
          </div>

          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2">
              <TrendingUp size={14} /> Query ultimi 30gg
            </div>
            <div className="text-3xl font-bold">{(last30.queries || 0).toLocaleString("it-IT")}</div>
            {(last30.cache_hits || 0) > 0 && (
              <p className="text-gray-500 text-xs mt-2">
                di cui {last30.cache_hits} da cache (cost. 0)
              </p>
            )}
          </div>

          {canSeeMargin && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2">
                <TrendingUp size={14} /> Margine ultimi 30gg
              </div>
              <div className="text-3xl font-bold text-emerald-400">{formatEur(last30.margin_eur)}</div>
              <p className="text-gray-500 text-xs mt-2">
                Ricavi {formatEur(last30.revenue_eur)} − costi {formatEur(last30.cost_eur)}
              </p>
            </div>
          )}
          {!canSeeMargin && (
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2">
                <CreditCard size={14} /> Spesa ultimi 30gg
              </div>
              <div className="text-3xl font-bold">{formatEur(last30.revenue_eur)}</div>
              <p className="text-gray-500 text-xs mt-2">Costo cliente (markup incluso)</p>
            </div>
          )}
        </div>

        {/* Burn-rate forecast widget (up-sell hint, non-invasive) */}
        {forecast && (
          <BurnForecastCard
            forecast={forecast}
            packages={packagesPublic || []}
            onSuggestUpgrade={(pkgId) => {
              // Scroll to the suggested package card and pulse it.
              const el = document.querySelector(`[data-testid="package-${pkgId}"]`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("ring-2", "ring-lojack-clause-400");
                setTimeout(() => el.classList.remove("ring-2", "ring-lojack-clause-400"), 2400);
              }
            }}
          />
        )}

        {/* Hall of Fame — top dealers ranked by consumption + ROI */}
        {hallOfFame && (
          <HallOfFameCard
            data={hallOfFame}
            loading={hofLoading}
            onChangeMonth={loadHallOfFame}
          />
        )}

        {/* Dealer enablement — per-dealer Stripe purchase toggle */}
        {dealersVinPro && (
          <DealerEnablementCard
            data={dealersVinPro}
            onToggle={toggleDealerVinPro}
          />
        )}

        {/* Pricing config — TelcaVoIP confidential, gated by email domain */}
        {canSeeMargin && settings && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="pricing-config-card">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><SettingsIcon size={16} /> Configurazione pricing (TelcaVoIP)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="text-gray-500 text-xs">Costo provider / query</label>
                <input type="number" step="0.01" defaultValue={settings.cost_per_query_eur}
                  onBlur={e => axios.put(`${API}/admin/vin-decoder/settings`, { cost_per_query_eur: parseFloat(e.target.value) })
                    .then(() => toast.success("Costo aggiornato"))
                    .then(refreshAll)
                    .catch(() => toast.error("Errore"))}
                  className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2"
                  data-testid="cost-per-query-input"
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs">Prezzo verso LoJack / query</label>
                <input type="number" step="0.01" defaultValue={settings.markup_price_eur}
                  onBlur={e => axios.put(`${API}/admin/vin-decoder/settings`, { markup_price_eur: parseFloat(e.target.value) })
                    .then(() => toast.success("Prezzo aggiornato"))
                    .then(refreshAll)
                    .catch(() => toast.error("Errore"))}
                  className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2"
                  data-testid="markup-price-input"
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs">Soglia alert saldo basso</label>
                <input type="number" defaultValue={settings.low_balance_threshold}
                  onBlur={e => axios.put(`${API}/admin/vin-decoder/settings`, { low_balance_threshold: parseInt(e.target.value, 10) })
                    .then(() => toast.success("Soglia aggiornata"))
                    .then(refreshAll)
                    .catch(() => toast.error("Errore"))}
                  className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2"
                  data-testid="low-balance-threshold-input"
                />
              </div>
            </div>
            <p className="text-gray-500 text-[11px] mt-3">
              Margine per query: <strong className="text-emerald-400">{formatEur((settings.markup_price_eur || 0) - (settings.cost_per_query_eur || 0))}</strong>
              ({(((settings.markup_price_eur || 0) / (settings.cost_per_query_eur || 1) * 100) - 100).toFixed(0)}% markup)
            </p>
          </div>
        )}

        {/* Packages catalogue (visible to ALL admins, no cost/margin leak) */}
        {canBuyPackages && packagesPublic.length > 0 && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="packages-card">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><CreditCard size={16} /> Pacchetti prepagati</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {packagesPublic.map(pkg => (
                <div key={pkg.id} className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4 flex flex-col" data-testid={`package-${pkg.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <strong>{pkg.name}</strong>
                    {pkg.monthly && <span className="text-[10px] uppercase bg-lojack-blue-500/20 text-lojack-blue-400 px-2 py-0.5 rounded-full">Ricorrente</span>}
                  </div>
                  <p className="text-3xl font-bold text-lojack-blue-400">{pkg.queries.toLocaleString("it-IT")}</p>
                  <p className="text-gray-500 text-xs">query{pkg.monthly ? " / mese" : ""}</p>
                  <p className="mt-3 text-2xl font-semibold">{formatEur(pkg.price_eur)}</p>
                  <p className="text-gray-500 text-xs mb-3">{formatEur(pkg.price_eur / pkg.queries)} / query</p>
                  <button
                    onClick={() => buyPackage(pkg)}
                    disabled={topupBusy === pkg.id}
                    className="mt-auto px-3 py-2 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                    data-testid={`buy-${pkg.id}`}
                  >
                    {topupBusy === pkg.id ? (
                      <><Loader2 size={14} className="animate-spin" /> Avvio Stripe…</>
                    ) : (
                      <><CreditCard size={14} /> Acquista con Stripe</>
                    )}
                  </button>
                </div>
              ))}
            </div>
            {!canSeeMargin && (
              <p className="text-gray-500 text-[11px] mt-3">
                Pagamento sicuro tramite Stripe. I crediti vengono accreditati automaticamente al ritorno.
                La fattura sarà emessa da TelcaVoIP separatamente.
              </p>
            )}
          </div>
        )}

        {/* Transactions log */}
        {canBuyPackages && transactions.length > 0 && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="transactions-card">
            <h3 className="font-semibold mb-4">Ultime transazioni</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-[11px] uppercase tracking-wider">
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2">Data</th>
                    <th className="text-left py-2">Pacchetto</th>
                    <th className="text-right py-2">Crediti</th>
                    <th className="text-right py-2">Prezzo</th>
                    <th className="text-left py-2">Acquirente</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, i) => (
                    <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                      <td className="py-2 text-gray-400">{new Date(tx.created_at).toLocaleString("it-IT")}</td>
                      <td className="py-2">{tx.package_id}</td>
                      <td className="py-2 text-right text-lojack-blue-400">+{tx.queries}</td>
                      <td className="py-2 text-right">{formatEur(tx.price_eur)}</td>
                      <td className="py-2 text-gray-400 text-xs">{tx.purchaser_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!enabled && (
          <div className="bg-lojack-clause-500/5 border border-lojack-clause-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="disabled-banner">
            <AlertCircle size={18} className="text-lojack-clause-400 shrink-0 mt-0.5" />
            <div className="text-sm text-lojack-clause-300">
              <strong>Servizio in standby.</strong> Il bottone "Decodifica completa VIN" è nascosto nel portale dealer. Resta attivo il WMI offline gratuito che riconosce automaticamente marca + paese di costruzione per tutti i VIN europei.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VinDecoderProPage;

// ── BurnForecastCard ─────────────────────────────────────────────
// Non-invasive consumption widget driven by /admin/vin-decoder/burn-forecast.
// Severity → visual tier mapping (matches backend rules):
//   idle      → muted gray, "Nessun consumo nella finestra"
//   ok        → emerald, ">21gg di autonomia"
//   watch     → blue, "10-21gg, pianifica il prossimo acquisto"
//   warn      → amber, "5-10gg, considera l'upgrade"
//   critical  → red, "<5gg, acquista ora"
//   empty     → red, "Saldo esaurito"
const _SEV_STYLES = {
  idle:     { bar: "bg-gray-500/40", text: "text-gray-400", badge: "bg-gray-500/15 text-gray-400", border: "border-gray-700" },
  ok:       { bar: "bg-emerald-500",  text: "text-emerald-400", badge: "bg-emerald-500/15 text-emerald-400", border: "border-emerald-500/30" },
  watch:    { bar: "bg-lojack-blue-500", text: "text-lojack-blue-400", badge: "bg-lojack-blue-500/15 text-lojack-blue-400", border: "border-lojack-blue-500/30" },
  warn:     { bar: "bg-lojack-clause-500",     text: "text-lojack-clause-400", badge: "bg-lojack-clause-500/15 text-lojack-clause-400", border: "border-lojack-clause-500/30" },
  critical: { bar: "bg-red-500",       text: "text-red-400",   badge: "bg-red-500/15 text-red-400", border: "border-red-500/40" },
  empty:    { bar: "bg-red-500",       text: "text-red-400",   badge: "bg-red-500/15 text-red-400", border: "border-red-500/40" },
};

const _SEV_LABELS = {
  idle: "Nessun consumo recente",
  ok: "Autonomia OK",
  watch: "Pianifica riacquisto",
  warn: "Soglia attenzione",
  critical: "Riacquisto urgente",
  empty: "Saldo esaurito",
};

function BurnForecastCard({ forecast, packages, onSuggestUpgrade }) {
  const sev = forecast.severity || "idle";
  const style = _SEV_STYLES[sev] || _SEV_STYLES.idle;
  const label = _SEV_LABELS[sev] || sev;
  const suggested = packages.find((p) => p.id === forecast.suggested_package_id);

  const series = forecast.daily_series || [];
  const maxQ = Math.max(1, ...series.map((d) => d.queries));

  // Build the human-readable runway sentence.
  let headline;
  if (sev === "empty") {
    headline = "Il saldo è azzerato. Acquista un pacchetto per riprendere il servizio.";
  } else if (sev === "idle") {
    headline = "Negli ultimi 7 giorni nessuna richiesta ha decrementato il saldo.";
  } else {
    const days = forecast.days_remaining ?? 0;
    const dateStr = forecast.exhausted_at_iso
      ? new Date(forecast.exhausted_at_iso).toLocaleDateString("it-IT", { day: "numeric", month: "long" })
      : null;
    headline = `Al ritmo attuale (${forecast.avg_daily_queries.toFixed(1)} q/giorno), esaurirai i crediti in ~${days.toFixed(1)} giorni${dateStr ? ` (entro il ${dateStr})` : ""}.`;
  }

  return (
    <div
      className={`bg-[#111] border ${style.border} rounded-2xl p-5 space-y-4`}
      data-testid="burn-forecast-card"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${style.badge}`}>
          <CalendarClock size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">Previsione consumo (ultimi 7 giorni)</h3>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${style.badge}`}
              data-testid="burn-forecast-severity"
            >
              {label}
            </span>
          </div>
          <p className="text-gray-300 text-sm mt-1" data-testid="burn-forecast-headline">{headline}</p>
        </div>
      </div>

      {/* Daily sparkline (7 bars) */}
      <div className="flex items-end gap-1 h-12" data-testid="burn-forecast-sparkline">
        {series.map((d) => {
          const h = Math.max(2, Math.round((d.queries / maxQ) * 44));
          return (
            <div key={d.date_iso} className="flex-1 flex flex-col items-center justify-end" title={`${d.date_iso}: ${d.queries} q`}>
              <div className={`w-full rounded-t ${style.bar}`} style={{ height: `${h}px`, opacity: d.queries === 0 ? 0.25 : 1 }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500 font-mono">
        <span>{series[0]?.date_iso?.slice(5) || ""}</span>
        <span className="text-gray-400">{forecast.window_queries} query in 7gg · saldo {forecast.balance.toLocaleString("it-IT")}</span>
        <span>{series[series.length - 1]?.date_iso?.slice(5) || "oggi"}</span>
      </div>

      {/* Up-sell CTA — only when forecast actually suggests an upgrade */}
      {suggested && (sev === "warn" || sev === "critical") && (
        <button
          onClick={() => onSuggestUpgrade(suggested.id)}
          className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium border ${style.border} ${style.text} hover:bg-white/5 transition flex items-center justify-center gap-2`}
          data-testid="burn-forecast-suggest-cta"
        >
          <TrendingUp size={14} />
          Considera il piano <strong>{suggested.name}</strong> ({suggested.queries.toLocaleString("it-IT")} query/mese)
        </button>
      )}
    </div>
  );
}

// ── HallOfFameCard ─────────────────────────────────────────────
// Monthly leaderboard of the top dealers ranked by VIN Decoder Pro
// consumption + conversion-to-signed-contract rate. Sales-coaching
// anchor: lets LoJack spot high-ROI dealers to incentivise and
// low-ROI ones to coach.

const _PODIUM_ICONS = [
  { icon: Trophy, color: "text-lojack-clause-300",  bar: "bg-lojack-clause-300",  bg: "bg-lojack-clause-500/15",  border: "border-lojack-clause-500/40" },  // 1st — gold
  { icon: Award,  color: "text-gray-300",   bar: "bg-gray-300",   bg: "bg-gray-400/15",   border: "border-gray-400/40" },   // 2nd — silver
  { icon: Award,  color: "text-orange-400", bar: "bg-orange-400", bg: "bg-orange-500/15", border: "border-orange-500/40" }, // 3rd — bronze
];

function _shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
}

function HallOfFameCard({ data, loading, onChangeMonth }) {
  const items = data.items || [];
  const isEmpty = items.length === 0;
  const today = new Date();
  const currentYM = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}`;
  const isCurrentMonth = data.month === currentYM;

  return (
    <div
      className="bg-[#111] border border-gray-800 rounded-2xl p-5 space-y-4"
      data-testid="hall-of-fame-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-300 flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold flex items-center gap-2 flex-wrap">
              Hall of Fame
              <span className="text-[10px] uppercase tracking-wider text-lojack-clause-300/80 bg-lojack-clause-500/10 px-2 py-0.5 rounded-full font-semibold">
                Top dealer · {data.month_label}
              </span>
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Classifica mensile per consumo VIN Decoder Pro + conversion rate verso contratti firmati.
              Usa questi numeri per riconoscere chi performa e per coachare chi resta indietro.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onChangeMonth(_shiftMonth(data.month, -1))}
            disabled={loading}
            className="p-1.5 rounded-lg border border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800/40 disabled:opacity-40"
            data-testid="hof-prev-month"
            title="Mese precedente"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="text-xs text-gray-300 font-mono w-20 text-center" data-testid="hof-month-value">
            {data.month}
          </div>
          <button
            onClick={() => onChangeMonth(_shiftMonth(data.month, +1))}
            disabled={loading || isCurrentMonth}
            className="p-1.5 rounded-lg border border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800/40 disabled:opacity-40"
            data-testid="hof-next-month"
            title={isCurrentMonth ? "Mese in corso" : "Mese successivo"}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Aggregate summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Query totali</div>
          <div className="text-lg font-bold text-lojack-clause-300" data-testid="hof-total-queries">
            {data.total_queries.toLocaleString("it-IT")}
          </div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Contratti firmati</div>
          <div className="text-lg font-bold text-lojack-blue-400" data-testid="hof-total-signed">
            {data.total_signed_contracts.toLocaleString("it-IT")}
          </div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Conv. rate medio</div>
          <div className="text-lg font-bold text-emerald-400" data-testid="hof-avg-conv">
            {data.total_queries > 0
              ? `${(data.total_signed_contracts / data.total_queries * 100).toFixed(1)} %`
              : "—"}
          </div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Dealer attivi</div>
          <div className="text-lg font-bold text-white" data-testid="hof-active-dealers">
            {items.length}
          </div>
        </div>
      </div>

      {/* Podium / list */}
      {isEmpty ? (
        <div className="text-center text-gray-500 text-sm py-8 italic" data-testid="hof-empty">
          Nessun dealer ha consumato crediti VIN in {data.month_label.toLowerCase()}.
        </div>
      ) : (
        <div className="space-y-2" data-testid="hof-leaderboard">
          {items.map((it) => {
            const podium = _PODIUM_ICONS[it.rank - 1];
            const Icon = podium?.icon;
            const isPodium = !!podium;
            return (
              <div
                key={it.dealer_id}
                className={`flex items-center gap-3 rounded-xl border ${isPodium ? podium.border : "border-gray-800"} ${isPodium ? podium.bg : "bg-black/20"} px-3 py-2.5`}
                data-testid={`hof-row-${it.rank}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPodium ? podium.color : "text-gray-400"} font-bold text-xs`}>
                  {Icon ? <Icon size={16} /> : `#${it.rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate" data-testid={`hof-name-${it.rank}`}>
                      {it.company_name}
                    </span>
                    {it.city && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                        <MapPin size={10} /> {it.city}
                      </span>
                    )}
                    {it.is_orphan && (
                      <span className="text-[9px] uppercase tracking-wider text-red-400/80 bg-red-500/10 px-1.5 py-0.5 rounded">
                        Dealer eliminato
                      </span>
                    )}
                  </div>
                  {it.contact_name && (
                    <div className="text-[11px] text-gray-500">{it.contact_name}</div>
                  )}
                  {/* Share-of-voice bar */}
                  <div className="mt-1.5 h-1 rounded-full bg-gray-800/60 overflow-hidden">
                    <div
                      className={`h-full ${isPodium ? podium.bar : "bg-gray-500"} rounded-full transition-all`}
                      style={{ width: `${Math.max(2, it.share_pct)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0 grid grid-cols-3 gap-3 items-center">
                  <div>
                    <div className="font-bold text-lojack-clause-300 text-sm" data-testid={`hof-queries-${it.rank}`}>
                      {it.queries.toLocaleString("it-IT")}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">query</div>
                  </div>
                  <div>
                    <div className="font-bold text-lojack-blue-400 text-sm" data-testid={`hof-signed-${it.rank}`}>
                      {it.signed_contracts}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">firme</div>
                  </div>
                  <div>
                    <div className={`font-bold text-sm ${it.conversion_pct == null ? "text-gray-500" : it.conversion_pct >= 50 ? "text-emerald-400" : it.conversion_pct >= 20 ? "text-lojack-clause-400" : "text-red-400"}`} data-testid={`hof-conv-${it.rank}`}>
                      {it.conversion_pct == null ? "—" : `${it.conversion_pct.toFixed(1)} %`}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">conv.</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <Loader2 size={12} className="animate-spin" /> Caricamento in corso…
        </div>
      )}
    </div>
  );
}


// ── DealerEnablementCard ───────────────────────────────────────
// Per-dealer toggle for autonomous VIN Pro purchasing on Stripe.
// Approved model (03/06/2026):
//   • All dealers start disabled → use LoJack central pool, 50q/month free
//   • Admin LoJack flips the switch when a dealer matures commercially
//   • Quota editable per-dealer (0–10000)
// Visible to admin + super_admin (Fabrizio's call).
function DealerEnablementCard({ data, onToggle }) {
  const [editing, setEditing] = useState(null); // dealer_id being edited
  const [quotaDraft, setQuotaDraft] = useState({});

  const items = data.items || [];
  const enabledCount = items.filter((d) => d.purchase_enabled).length;
  const disabledCount = items.length - enabledCount;
  const totalCentralUsed = items.reduce((acc, d) => acc + (d.central_used_this_month || 0), 0);

  const beginEdit = (dealer) => {
    setEditing(dealer.dealer_id);
    setQuotaDraft({ ...quotaDraft, [dealer.dealer_id]: dealer.central_monthly_quota });
  };

  const saveEdit = async (dealer) => {
    const newQuota = parseInt(quotaDraft[dealer.dealer_id], 10);
    if (Number.isNaN(newQuota) || newQuota < 0 || newQuota > 10000) {
      toast.error("Quota deve essere tra 0 e 10000");
      return;
    }
    await onToggle(dealer.dealer_id, {
      purchase_enabled: dealer.purchase_enabled,
      monthly_quota: newQuota,
      reason: "Aggiornamento quota mensile",
    });
    setEditing(null);
  };

  const toggleEnable = async (dealer, newEnabled) => {
    const reason = window.prompt(
      newEnabled
        ? `Abilitare "${dealer.company_name}" all'acquisto autonomo Stripe?\n\nMotivo (opzionale, finisce nell'audit log):`
        : `Disabilitare "${dealer.company_name}"? Tornerà ad usare il pozzo centrale LoJack con quota ${dealer.central_monthly_quota} q/mese.\n\nMotivo (opzionale):`,
      "",
    );
    if (reason === null) return; // cancelled
    await onToggle(dealer.dealer_id, {
      purchase_enabled: newEnabled,
      monthly_quota: dealer.central_monthly_quota,
      reason: reason || "",
    });
  };

  return (
    <div
      className="bg-[#111] border border-gray-800 rounded-2xl p-5 space-y-4"
      data-testid="dealer-enablement-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/15 text-lojack-blue-400 flex items-center justify-center shrink-0">
          <Store size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold flex items-center gap-2 flex-wrap">
            Abilitazione dealer all'acquisto autonomo
            <span className="text-[10px] uppercase tracking-wider text-lojack-blue-400/80 bg-lojack-blue-500/10 px-2 py-0.5 rounded-full font-semibold">
              {enabledCount}/{items.length} attivi
            </span>
          </h3>
          <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">
            Default: ogni dealer pesca dal <strong className="text-lojack-clause-300">pozzo LoJack centrale</strong>
            {" "}({data.default_monthly_quota} query/mese gratuite). Abilitando l'acquisto autonomo,
            il dealer comprerà i propri pacchetti direttamente da Stripe e non peserà più su LoJack.
          </p>
        </div>
      </div>

      {/* Aggregate summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Pozzo centrale LoJack</div>
          <div className="text-lg font-bold text-lojack-clause-300">
            {data.central_pool_balance.toLocaleString("it-IT")}
          </div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Dealer abilitati</div>
          <div className="text-lg font-bold text-emerald-400">{enabledCount}</div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Dealer free-tier</div>
          <div className="text-lg font-bold text-gray-300">{disabledCount}</div>
        </div>
        <div className="bg-black/30 border border-gray-800 rounded-xl px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Query free-tier consumate</div>
          <div className="text-lg font-bold text-lojack-blue-400">{totalCentralUsed}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-6 italic">
          Nessun dealer presente nel sistema.
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {items.map((d) => {
            const isEditing = editing === d.dealer_id;
            const usagePct = d.central_monthly_quota > 0
              ? Math.min(100, (d.central_used_this_month / d.central_monthly_quota) * 100)
              : 0;
            return (
              <div
                key={d.dealer_id}
                className={`flex items-center gap-3 rounded-xl border ${d.purchase_enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-gray-800 bg-black/20"} px-3 py-2.5`}
                data-testid={`dealer-row-${d.dealer_id}`}
              >
                {d.purchase_enabled ? (
                  <ShieldCheck size={18} className="text-emerald-400 shrink-0" />
                ) : (
                  <Lock size={16} className="text-gray-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate text-sm" data-testid={`dealer-name-${d.dealer_id}`}>
                      {d.company_name || "—"}
                    </span>
                    {d.city && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                        <MapPin size={10} /> {d.city}
                      </span>
                    )}
                    {d.country && (
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">{d.country}</span>
                    )}
                  </div>
                  {d.purchase_enabled ? (
                    <div className="text-[11px] text-emerald-400/90 mt-0.5">
                      Saldo personale: <strong>{d.personal_balance.toLocaleString("it-IT")}</strong> query · acquisto autonomo Stripe
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-500 mt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>Pozzo LoJack:</span>
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              min={0}
                              max={10000}
                              value={quotaDraft[d.dealer_id] ?? d.central_monthly_quota}
                              onChange={(e) => setQuotaDraft({ ...quotaDraft, [d.dealer_id]: e.target.value })}
                              className="bg-black border border-gray-700 rounded px-1.5 py-0.5 w-16 text-xs text-white"
                              data-testid={`dealer-quota-input-${d.dealer_id}`}
                            />
                            <button
                              onClick={() => saveEdit(d)}
                              className="text-emerald-400 hover:text-emerald-300 text-xs"
                              data-testid={`dealer-quota-save-${d.dealer_id}`}
                            >
                              Salva
                            </button>
                            <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-gray-300 text-xs">
                              Annulla
                            </button>
                          </>
                        ) : (
                          <>
                            <strong className="text-gray-300">{d.central_used_this_month}</strong>
                            <span>/</span>
                            <strong className="text-gray-300">{d.central_monthly_quota}</strong>
                            <span>q questo mese</span>
                            <button
                              onClick={() => beginEdit(d)}
                              className="text-lojack-blue-400 hover:text-lojack-blue-300 text-[10px] underline"
                              data-testid={`dealer-quota-edit-${d.dealer_id}`}
                            >
                              modifica
                            </button>
                          </>
                        )}
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-gray-800/60 overflow-hidden max-w-xs">
                        <div
                          className={`h-full ${usagePct >= 90 ? "bg-red-500" : usagePct >= 60 ? "bg-lojack-clause-400" : "bg-emerald-500"} transition-all`}
                          style={{ width: `${Math.max(2, usagePct)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => toggleEnable(d, !d.purchase_enabled)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    d.purchase_enabled
                      ? "bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
                      : "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30"
                  }`}
                  data-testid={`dealer-toggle-${d.dealer_id}`}
                >
                  {d.purchase_enabled ? "Disabilita" : "Abilita"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

